import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTooltip, TooltipSide } from './Tooltip';

/**
 * Text that clips, with the full string on hover/focus — but only when it
 * actually clipped. The overflow is measured (scroll size vs client size), so
 * a line that fits carries no tooltip at all: hovering short text stays quiet
 * instead of repeating what is already on screen.
 *
 * On a phone the single-line form wraps instead of clipping. The clip is only
 * acceptable because the tooltip gives the text back, and there is no hover on
 * a touch screen — clipping there is deletion, in the one place (a supplier's
 * own name, in a list of suppliers) where it removes the thing being chosen
 * between. The measurement then reports no overflow on its own, so no tooltip
 * is armed and nothing else has to know. The 2- and 3-line clamps keep their
 * shape: those cap a paragraph, and the first two lines of a paragraph still
 * say what it is about.
 */
export interface TruncatedTextProps {
  text: string;
  /** 1 = single line with an ellipsis; 2–3 = clamped block. */
  lines?: 1 | 2 | 3;
  as?: 'span' | 'div';
  side?: TooltipSide;
  /** False inside a button/link, where a nested tab stop would be invalid. */
  focusable?: boolean;
  className?: string;
}

// `line-clamp-*` carries its own `display`, so pairing it with `block` lets
// whichever rule Tailwind emits last win — and the clamp silently stops.
const CLIP_CLASS: Record<1 | 2 | 3, string> = {
  1: 'block truncate max-md:overflow-visible max-md:whitespace-normal',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
};

export const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  lines = 1,
  as = 'span',
  side = 'top',
  focusable = true,
  className = '',
}) => {
  const [clipped, setClipped] = useState(false);
  const nodeRef = useRef<HTMLElement | null>(null);
  const {
    anchorProps: { ref: setAnchor, ...handlers },
    bubble,
  } = useTooltip(text, side, clipped);

  const measure = useCallback(() => {
    const el = nodeRef.current;
    if (!el) return;
    setClipped(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);
  }, []);

  useLayoutEffect(measure, [measure, text, lines]);

  useEffect(() => {
    const el = nodeRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  const attachRef = (node: HTMLElement | null) => {
    nodeRef.current = node;
    setAnchor(node);
  };
  const props = {
    ...handlers,
    ref: attachRef,
    tabIndex: clipped && focusable ? 0 : undefined,
    className: `${CLIP_CLASS[lines]} focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1 ${className}`,
  };

  return as === 'div' ? (
    <div {...props}>
      {text}
      {bubble}
    </div>
  ) : (
    <span {...props}>
      {text}
      {bubble}
    </span>
  );
};

export default TruncatedText;
